// 高级筛选组件
// 提供更复杂的筛选条件设置功能
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Select,
  InputNumber,
  DatePicker,
  Button,
  Space,
  Row,
  Col,
  Card
} from 'antd';
import {
  FilterOutlined,
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { FIELD_MAPPING } from '../../config/field-mapping';
import type { FilterCondition } from '../../types/field-mapping';
import dayjs from 'dayjs';

const { Option } = Select;
const { RangePicker } = DatePicker;

export interface AdvancedFilterProps {
  visible: boolean;
  onClose: () => void;
  onApply: (filters: FilterCondition[]) => void;
  initialFilters?: FilterCondition[];
}

interface FilterRule {
  id: string;
  field: string;
  operator: string;
  value: any;
  label?: string;
}

const AdvancedFilter: React.FC<AdvancedFilterProps> = ({
  visible,
  onClose,
  onApply,
  initialFilters = []
}) => {
  const [filterRules, setFilterRules] = useState<FilterRule[]>([]);

  // 操作符选项
  const operatorOptions = [
    { value: 'eq', label: '等于' },
    { value: 'ne', label: '不等于' },
    { value: 'gt', label: '大于' },
    { value: 'gte', label: '大于等于' },
    { value: 'lt', label: '小于' },
    { value: 'lte', label: '小于等于' },
    { value: 'like', label: '包含' },
    { value: 'not_like', label: '不包含' },
    { value: 'in', label: '在范围内' },
    { value: 'not_in', label: '不在范围内' },
    { value: 'between', label: '介于' },
    { value: 'is_null', label: '为空' },
    { value: 'is_not_null', label: '不为空' }
  ];

  // 可筛选字段选项
  const filterableFields = Object.entries(FIELD_MAPPING)
    .filter(([_, config]) => config.filterable)
    .map(([key, config]) => ({
      value: key,
      label: config.label,
      type: config.type,
      group: config.group
    }));

  // 字段分组
  const fieldGroups = filterableFields.reduce((groups, field) => {
    const group = field.group;
    if (!groups[group]) {
      groups[group] = [];
    }
    groups[group].push(field);
    return groups;
  }, {} as Record<string, typeof filterableFields>);

  // 初始化筛选规则
  useEffect(() => {
    if (initialFilters.length > 0) {
      const rules = initialFilters.map((filter, index) => ({
        id: `rule_${index}`,
        field: filter.field,
        operator: filter.operator,
        value: filter.value,
        label: filter.label
      }));
      setFilterRules(rules);
    } else {
      // 添加默认规则
      addFilterRule();
    }
  }, [initialFilters]);



  // 添加筛选规则
  const addFilterRule = () => {
    const newRule: FilterRule = {
      id: `rule_${Date.now()}`,
      field: '',
      operator: 'eq',
      value: undefined
    };
    setFilterRules([...filterRules, newRule]);
  };

  // 删除筛选规则
  const removeFilterRule = (id: string) => {
    setFilterRules(filterRules.filter(rule => rule.id !== id));
  };

  // 更新筛选规则
  const updateFilterRule = (id: string, updates: Partial<FilterRule>) => {
    setFilterRules(filterRules.map(rule => 
      rule.id === id ? { ...rule, ...updates } : rule
    ));
  };

  // 获取字段的操作符选项
  const getOperatorOptions = (fieldType: string) => {
    switch (fieldType) {
      case 'number':
      case 'currency':
      case 'percentage':
        return operatorOptions.filter(op => 
          ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'between', 'is_null', 'is_not_null'].includes(op.value)
        );
      case 'text':
        return operatorOptions.filter(op => 
          ['eq', 'ne', 'like', 'not_like', 'in', 'not_in', 'is_null', 'is_not_null'].includes(op.value)
        );
      case 'select':
        return operatorOptions.filter(op => 
          ['eq', 'ne', 'in', 'not_in', 'is_null', 'is_not_null'].includes(op.value)
        );
      case 'date':
      case 'datetime':
        return operatorOptions.filter(op => 
          ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'between', 'is_null', 'is_not_null'].includes(op.value)
        );
      default:
        return operatorOptions;
    }
  };

  // 渲染值输入组件
  const renderValueInput = (rule: FilterRule) => {
    const fieldConfig = FIELD_MAPPING[rule.field];
    if (!fieldConfig) return null;

    const commonProps = {
      style: { width: '100%' },
      placeholder: '请输入值',
      value: rule.value,
      onChange: (value: any) => updateFilterRule(rule.id, { value })
    };

    // 如果操作符是 is_null 或 is_not_null，不需要值输入
    if (['is_null', 'is_not_null'].includes(rule.operator)) {
      return <span style={{ color: '#999' }}>无需输入值</span>;
    }

    switch (fieldConfig.type) {
      case 'select':
        return (
          <Select
            {...commonProps}
            mode={['in', 'not_in'].includes(rule.operator) ? 'multiple' : undefined}
            placeholder="请选择"
          >
            {fieldConfig.options?.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        );

      case 'number':
      case 'currency':
      case 'percentage':
        if (rule.operator === 'between') {
          return (
            <Space.Compact style={{ width: '100%' }}>
              <InputNumber
                placeholder="最小值"
                style={{ width: '50%' }}
                value={rule.value?.[0]}
                onChange={(val) => updateFilterRule(rule.id, { 
                  value: [val, rule.value?.[1]] 
                })}
              />
              <InputNumber
                placeholder="最大值"
                style={{ width: '50%' }}
                value={rule.value?.[1]}
                onChange={(val) => updateFilterRule(rule.id, { 
                  value: [rule.value?.[0], val] 
                })}
              />
            </Space.Compact>
          );
        }
        return (
          <InputNumber
            {...commonProps}
            precision={fieldConfig.precision}
            min={fieldConfig.range?.[0]}
            max={fieldConfig.range?.[1]}
          />
        );

      case 'date':
        if (rule.operator === 'between') {
          return (
            <RangePicker
              style={{ width: '100%' }}
              value={rule.value ? [dayjs(rule.value[0]), dayjs(rule.value[1])] : undefined}
              onChange={(dates) => updateFilterRule(rule.id, { 
                value: dates ? [dates[0]?.format('YYYY-MM-DD'), dates[1]?.format('YYYY-MM-DD')] : undefined
              })}
            />
          );
        }
        return (
          <DatePicker
            {...commonProps}
            value={rule.value ? dayjs(rule.value) : undefined}
            onChange={(date) => updateFilterRule(rule.id, { 
              value: date?.format('YYYY-MM-DD') 
            })}
          />
        );

      default:
        return (
          <Select
            {...commonProps}
            mode={['in', 'not_in'].includes(rule.operator) ? 'tags' : undefined}
            placeholder="请输入"
          />
        );
    }
  };

  // 应用筛选
  const handleApply = () => {
    const validRules = filterRules.filter(rule => 
      rule.field && rule.operator && (
        ['is_null', 'is_not_null'].includes(rule.operator) || 
        rule.value !== undefined && rule.value !== null && rule.value !== ''
      )
    );

    const filters: FilterCondition[] = validRules.map(rule => {
      const fieldConfig = FIELD_MAPPING[rule.field];
      return {
        field: rule.field,
        operator: rule.operator as any,
        value: rule.value,
        label: `${fieldConfig?.label}: ${rule.operator === 'between' ? 
          `${rule.value[0]} - ${rule.value[1]}` : 
          Array.isArray(rule.value) ? rule.value.join(', ') : rule.value}`
      };
    });

    onApply(filters);
    onClose();
  };

  // 重置筛选
  const handleReset = () => {
    setFilterRules([]);
    addFilterRule();
  };

  return (
    <Modal
      title={
        <Space>
          <FilterOutlined />
          高级筛选
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="reset" icon={<ReloadOutlined />} onClick={handleReset}>
          重置
        </Button>,
        <Button key="cancel" onClick={onClose}>
          取消
        </Button>,
        <Button key="apply" type="primary" icon={<SaveOutlined />} onClick={handleApply}>
          应用筛选
        </Button>
      ]}
    >
      <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        {filterRules.map((rule, index) => (
          <Card
            key={rule.id}
            size="small"
            style={{ marginBottom: 16 }}
            title={`筛选条件 ${index + 1}`}
            extra={
              filterRules.length > 1 && (
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => removeFilterRule(rule.id)}
                >
                  删除
                </Button>
              )
            }
          >
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Select
                  placeholder="选择字段"
                  style={{ width: '100%' }}
                  value={rule.field}
                  onChange={(value) => updateFilterRule(rule.id, { 
                    field: value, 
                    operator: 'eq', 
                    value: undefined 
                  })}
                  showSearch
                  optionFilterProp="children"
                >
                  {Object.entries(fieldGroups).map(([groupKey, fields]) => (
                    <Select.OptGroup key={groupKey} label={groupKey}>
                      {fields.map(field => (
                        <Option key={field.value} value={field.value}>
                          {field.label}
                        </Option>
                      ))}
                    </Select.OptGroup>
                  ))}
                </Select>
              </Col>
              <Col span={6}>
                <Select
                  placeholder="操作符"
                  style={{ width: '100%' }}
                  value={rule.operator}
                  onChange={(value) => updateFilterRule(rule.id, { 
                    operator: value, 
                    value: undefined 
                  })}
                  disabled={!rule.field}
                >
                  {rule.field && getOperatorOptions(FIELD_MAPPING[rule.field]?.type || 'text').map(op => (
                    <Option key={op.value} value={op.value}>
                      {op.label}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col span={10}>
                {rule.field && rule.operator && renderValueInput(rule)}
              </Col>
            </Row>
          </Card>
        ))}

        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={addFilterRule}
          style={{ width: '100%', marginTop: 16 }}
        >
          添加筛选条件
        </Button>
      </div>
    </Modal>
  );
};

export default AdvancedFilter;
